import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../../utils/apiClient';
import { enterLiveClass } from '../../../utils/liveClassRouting';
import { useSocket } from '../../../contexts/SocketContext';
import { AppButton, Callout, ConfirmModal } from '../../ui';
import Shimmer from '../../Common/Shimmer';
import CommonToaster from '../../Common/CommonToaster';
import PreMeetingCheck from '../../Classroom/PreMeetingCheck';

import useTrainerDashboard from './hooks/useTrainerDashboard';
import useMinuteClock from './hooks/useMinuteClock';
import { buildAgenda, clockHM, daySummary } from './trainerDashboardUtils';
import DashboardGreeting from './cards/DashboardGreeting';
import TrainerVitals from './cards/TrainerVitals';
import TodayAgendaCard from './cards/TodayAgendaCard';
import RecentActivityCard from './cards/RecentActivityCard';
import BatchesCard from './cards/BatchesCard';
import DemoSessionsCard from './cards/DemoSessionsCard';

/** Placeholders in the shape of the page, so nothing jumps when the data lands. */
function TrainerDashboardSkeleton() {
  return (
    <div className="tw-flex tw-flex-col tw-gap-5" aria-busy="true" aria-label="Loading your dashboard">
      <Shimmer height={52} />
      <div className="tw-grid tw-grid-cols-2 tw-gap-4 min-[992px]:tw-grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <Shimmer key={i} height={104} />)}
      </div>
      <div className="tw-grid tw-gap-5 min-[1200px]:tw-grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Shimmer height={280} />
        <Shimmer height={280} />
      </div>
      <Shimmer height={220} />
    </div>
  );
}

/**
 * The trainer's home (`/i/`): today's classes with the one to start or rejoin,
 * four numbers, recent submissions, and every ongoing batch.
 *
 * Live-class control lives here, not in the cards: starting runs the
 * pre-meeting check, ending is confirmed, and both refetch the summary.
 */
const TrainerDashboardPage = () => {
  const { data, loading, error, refetch } = useTrainerDashboard();
  const { socket } = useSocket() || {};
  const now = useMinuteClock();

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmAction, setConfirmAction] = useState(null);
  const [startMeetingBatch, setStartMeetingBatch] = useState(null);

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });

  // A class started or ended elsewhere (another tab, an admin) flips isLive.
  useEffect(() => {
    if (!socket) return undefined;
    const handler = (n) => {
      if (!n?.batchId) return;
      if (n.type !== 'class_started' && n.type !== 'class_ended') return;
      refetch();
    };
    socket.on('notification', handler);
    return () => socket.off('notification', handler);
  }, [socket, refetch]);

  const nowHM = clockHM(now);
  const agenda = useMemo(
    () => (data ? buildAgenda({ today: data.today, batches: data.batches, now: nowHM }) : []),
    [data, nowHM],
  );

  const startMeeting = (batch, sessionTopic) => {
    apiClient.post(`/batches/${batch._id}/start-meeting`, { sessionTopic: sessionTopic || '' })
      .then(({ data: res }) => {
        showToast('Class started.');
        enterLiveClass(batch._id, res, batch);
        refetch();
      })
      .catch((err) => showToast(err?.response?.data?.message || 'Could not start the class.', 'danger'));
  };

  const endMeeting = (batch) => {
    apiClient.post(`/batches/${batch._id}/end-meeting`)
      .then(() => {
        showToast('Class ended.');
        refetch();
      })
      .catch(() => showToast('Could not end the class.', 'danger'));
  };

  const handleStart = (batch) => {
    if (batch.status !== 'Ongoing') {
      setConfirmAction({
        type: 'statusWarning',
        title: 'This class cannot start yet',
        message: `${batch.name} is "${batch.status}". Ask your admin to mark it ongoing, then start the class.`,
        variant: 'warning',
        confirmText: 'OK',
      });
      return;
    }
    setStartMeetingBatch(batch);
  };

  const handleEnd = (batch) => setConfirmAction({
    type: 'endMeeting',
    batch,
    title: 'End the class?',
    message: `This ends ${batch.name} for everyone in the room.`,
    variant: 'warning',
    confirmText: 'End class',
  });

  if (loading && !data) return <TrainerDashboardSkeleton />;
  if (!data) {
    return (
      <Callout
        tone="danger"
        role="alert"
        action={<AppButton size="xs" variant="outline" onClick={refetch}>Try again</AppButton>}
      >
        {error?.response?.data?.message || 'Your dashboard could not be loaded. Check your connection and try again.'}
      </Callout>
    );
  }

  const { profile, stats, today, batches, recentActivity, analytics, attendanceTrend } = data;

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <DashboardGreeting profile={profile} summary={daySummary(today, agenda)} now={now} />

      <TrainerVitals
        stats={stats}
        analytics={analytics}
        trend={attendanceTrend}
        pending={today.assignmentsPending || 0}
      />

      <div className="tw-grid tw-items-start tw-gap-5 min-[1200px]:tw-grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <TodayAgendaCard
          agenda={agenda}
          isTomorrow={!!today.isTomorrow}
          dayName={today.scheduleDay}
          now={nowHM}
          onStart={handleStart}
          onEnd={handleEnd}
        />
        <RecentActivityCard items={recentActivity} />
      </div>

      <BatchesCard batches={batches} onStart={handleStart} onEnd={handleEnd} />

      <DemoSessionsCard onToast={showToast} />
      <DemoSessionsCard kind="webinar" onToast={showToast} />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PreMeetingCheck
        show={!!startMeetingBatch}
        role="instructor"
        batchName={startMeetingBatch?.name}
        batchId={startMeetingBatch?._id}
        confirmText="Start class"
        onReady={(topic) => {
          const batch = startMeetingBatch;
          setStartMeetingBatch(null);
          startMeeting(batch, topic);
        }}
        onCancel={() => setStartMeetingBatch(null)}
      />

      <ConfirmModal
        show={!!confirmAction}
        onHide={() => setConfirmAction(null)}
        onConfirm={() => {
          const action = confirmAction;
          setConfirmAction(null);
          if (action?.type === 'endMeeting') endMeeting(action.batch);
        }}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText={confirmAction?.confirmText || 'Confirm'}
        cancelText={confirmAction?.type === 'statusWarning' ? 'Close' : 'Cancel'}
        variant={confirmAction?.variant || 'info'}
      />
    </div>
  );
};

export default TrainerDashboardPage;
